
import { toJS, makeAutoObservable, runInAction } from 'mobx'
import { decorate, inject, injectable } from 'inversify'

// evertel
import { SessionService } from '@evertel/session/SessionService'
import { BlueUserStore } from '@evertel/blue-user'
import { Api } from '@evertel/api'

const version = 2

const defaultFrequent = [
    { id: '👍', text: '👍', usedCount: 0, lastUsed: new Date() },
    { id: '👎', text: '👎', usedCount: 0, lastUsed: new Date() },
    { id: '✅', text: '✅', usedCount: 0, lastUsed: new Date() },
    { id: '😀', text: '😀', usedCount: 0, lastUsed: new Date() },
    { id: '👊', text: '👊', usedCount: 0, lastUsed: new Date() },
    { id: '🙏', text: '🙏', usedCount: 0, lastUsed: new Date() },
    { id: '😂', text: '😂', usedCount: 0, lastUsed: new Date() }
]

class CurrentUserEmojisController {
    constructor(
        private session: SessionService,
        private userStore: BlueUserStore,
        private api: Api
    ) {
        makeAutoObservable(this)
    }

    get frequent() {
        const user = this.userStore.findById(this.session.currentUserId)

        // Return defaults if:
        // 1. No stored emoji data exists
        // 2. Version doesn't match current version
        // 3. No emojis array exists
        const storedEmojiData = user?.meta?.frequentEmojis
        return (storedEmojiData?.version === version && storedEmojiData?.emojis?.length)
            ? toJS(storedEmojiData.emojis)
            : [...defaultFrequent]
    }

    get skinTone() {
        const user = this.userStore.findById(this.session.currentUserId)

        return user?.meta?.frequentEmojis?.skinTone || 1
    }

    updateUserData = async (emojiData: any) => {
        if (!emojiData) return

        const userData = await this.api.Routes.BlueUser.putById(this.session.currentUserId, emojiData)

        runInAction(() => {
            this.userStore.update(userData)
        })
    }

    getFrequent(top = 5, includeCustom = true) {
        const sorted = this.frequent.slice().sort((a, b) => b.usedCount - a.usedCount)
        const newDefaults = defaultFrequent.filter(def =>
            !sorted.some(freq => freq.text === def.text)
        )
        const unique = [...sorted, ...newDefaults]

        return includeCustom
            ? unique.slice(0, top)
            : unique.filter(f => !f.text.includes(':')).slice(0, top)
    }

    incrementFrequent = async (text: string) => {
        const emojiIdx = this.frequent.findIndex(e => e.text === text)
        const updatedEmojis = [...this.frequent] // Create new array since frequent is a getter

        if (emojiIdx !== -1) {
            updatedEmojis[emojiIdx] = {
                ...updatedEmojis[emojiIdx],
                usedCount: updatedEmojis[emojiIdx].usedCount + 1,
                lastUsed: new Date()
            }
        } else {
            updatedEmojis.push({
                id: text,
                text: text,
                usedCount: 1,
                lastUsed: new Date()
            })
        }

        // update server
        await this.updateUserData({
            meta: {
                frequentEmojis: {
                    emojis: updatedEmojis,
                    skinTone: this.skinTone,
                    version: version
                }
            }
        })
    }

    decrementFrequent = async (text: string) => {
        const emojiIdx = this.frequent.findIndex(e => e.text === text)

        if (emojiIdx !== -1) {
            const updatedEmojis = [...this.frequent]
            updatedEmojis[emojiIdx] = {
                ...updatedEmojis[emojiIdx],
                usedCount: updatedEmojis[emojiIdx].usedCount - 1
            }

            await this.updateUserData({
                meta: {
                    frequentEmojis: {
                        emojis: updatedEmojis,
                        skinTone: this.skinTone,
                        version: version
                    }
                }
            })
        }
    }

    setSkinTone = async (skin: 1 | 2 | 3 | 4 | 5 | 6) => {
        if (!skin) {
            console.error('setSkinTone is missing the skin param (1-6)')
            return
        }

        this.updateUserData({
            meta: {
                frequentEmojis: {
                    emojis: this.frequent,
                    skinTone: skin,
                    version: version
                }
            }
        })
    }
}

decorate(injectable(), CurrentUserEmojisController)
decorate(inject(SessionService), CurrentUserEmojisController, 0)
decorate(inject(BlueUserStore), CurrentUserEmojisController, 1)
decorate(inject(Api), CurrentUserEmojisController, 2)


export { CurrentUserEmojisController }
